import { useState, useEffect } from 'react';
import { ADMIN_ENDPOINTS, adminFetch } from '../config';
import AdminImageUpload from './AdminImageUpload';
import './Admin.css';

const UPLOAD_PREFIX = '/admin/uploads/';
function imgUrl(src) {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return src.startsWith('uploads/') ? `/admin/${src}` : UPLOAD_PREFIX + src.replace(/^uploads\//, '');
}

export default function AdminServices() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ label_text: '', description_text: '', image_src: '', uri: '', sort_order: 0, is_visible: 1 });
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const reorderServices = async (newOrder) => {
    const orders = newOrder.map((s, i) => ({ id: s.id, sort_order: i }));
    const res = await adminFetch(ADMIN_ENDPOINTS.reorderServices, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orders }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
  };

  const load = () => {
    adminFetch(ADMIN_ENDPOINTS.fetchServicesAdmin)
      .then((r) => r.json())
      .then((data) => {
        if (!data.error) setList(Array.isArray(data) ? data : []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = (item) => {
    setEdit(item?.id || null);
    setForm({
      label_text: item?.label_text || '',
      description_text: item?.description_text || '',
      image_src: item?.image_src || '',
      uri: item?.uri || '',
      sort_order: item?.sort_order ?? 0,
      is_visible: item?.is_visible ?? 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminFetch(ADMIN_ENDPOINTS.saveService, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: edit || undefined,
          label_text: form.label_text,
          description_text: form.description_text,
          image_src: form.image_src,
          uri: form.uri,
          sort_order: form.sort_order,
          is_visible: form.is_visible ? 1 : 0,
        }),
      });
      const data = await res.json();
      if (data.error) alert(data.error);
      else {
        load();
        setEdit(null);
        setForm({ label_text: '', description_text: '', image_src: '', uri: '', sort_order: 0, is_visible: 1 });
      }
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    const res = await adminFetch(ADMIN_ENDPOINTS.deleteService, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    const data = await res.json();
    if (data.ok) load();
    else alert(data.error || 'Delete failed');
  };

  if (loading) return <p>Loading…</p>;

  return (
    <>
      <h1>Services</h1>
      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr><th style={{ width: '28px' }} aria-label="Drag to reorder" /><th>Label</th><th>URI</th><th>Order</th><th>Visible</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {list.map((s, index) => (
              <tr
                key={s.id}
                draggable
                onDragStart={(e) => {
                  setDragIndex(index);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(index));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverIndex(index);
                }}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragIndex;
                  const to = dragOverIndex ?? index;
                  setDragIndex(null);
                  setDragOverIndex(null);
                  if (from === to || from == null) return;
                  const next = [...list];
                  const [item] = next.splice(from, 1);
                  next.splice(to, 0, item);
                  setList(next);
                  reorderServices(next).catch((err) => alert(err.message || 'Failed to save order'));
                }}
                onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                className={dragOverIndex === index ? 'admin-row-drag-over' : ''}
              >
                <td className="admin-drag-handle" title="Drag to reorder">⋮⋮</td>
                <td>
                  <div className="admin-thumb-wrap">
                    <span className="admin-thumb-cell">
                      {s.image_src ? (
                        <img src={imgUrl(s.image_src)} alt="" className="admin-thumb" onError={(e) => { e.target.style.visibility = 'hidden'; e.target.style.width = '44px'; e.target.style.height = '44px'; }} />
                      ) : (
                        <span className="admin-thumb admin-thumb--placeholder" aria-hidden />
                      )}
                    </span>
                    {s.label_text}
                  </div>
                </td>
                <td>{s.uri}</td>
                <td>{s.sort_order}</td>
                <td>{s.is_visible ? 'Yes' : 'No'}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => openEdit(s)}>Edit</button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h2>{edit ? 'Edit Service' : 'Add Service'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Label</label>
            <input type="text" value={form.label_text} onChange={(e) => setForm((f) => ({ ...f, label_text: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Description (HTML)</label>
            <textarea rows={4} value={form.description_text} onChange={(e) => setForm((f) => ({ ...f, description_text: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Image</label>
            <AdminImageUpload
              value={form.image_src}
              onChange={(v) => setForm((f) => ({ ...f, image_src: v }))}
              uploadUrl={ADMIN_ENDPOINTS.upload}
              onUploadSuccess={(path) => {
                setForm((f) => ({ ...f, image_src: path }));
                if (edit != null) {
                  setList((prev) => prev.map((s) => (s.id === edit ? { ...s, image_src: path } : s)));
                }
              }}
              previewUrlResolver={imgUrl}
              placeholder="Or enter URL / uploads/..."
            />
          </div>
          <div className="form-group">
            <label>URI (slug)</label>
            <input type="text" value={form.uri} onChange={(e) => setForm((f) => ({ ...f, uri: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Sort order</label>
            <input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} />
          </div>
          <div className="form-group checkbox-group">
            <label><input type="checkbox" checked={!!form.is_visible} onChange={(e) => setForm((f) => ({ ...f, is_visible: e.target.checked ? 1 : 0 }))} /> Visible</label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn" disabled={saving}>{edit ? 'Update' : 'Add'}</button>
            {edit && <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(null)}>Cancel</button>}
          </div>
        </form>
      </div>
    </>
  );
}
